import React, { useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Nav,
  Offcanvas,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowLeft,
  ChevronRight,
  HelpCircle,
  RefreshCw,
  Search,
} from "react-feather";
import { Link, useSearchParams } from "react-router";
import Select from "react-select";
import {
  addDays,
  endOfDay,
  format,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGet } from "../../api";
import {
  IUser,
  IWorkPipelineActivityEntry,
  IWorkPipelineProductivityRow,
  WORK_PIPELINE_STAGE_LABELS,
} from "../../interfaces";

interface IActivityResponse {
  results: IWorkPipelineActivityEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface IPersonSummary {
  actor: string;
  name: string;
  completed: number;
  hunts: number;
  edits: number;
  activeMinutes: number;
  sessions: number;
  domainsTouched: number;
  output: number;
  outputPerHour: number;
  buckets: Record<string, number>;
  bucketMinutes: Record<string, number>;
  rows: IWorkPipelineProductivityRow[];
}

type ProductivityTab = "overview" | "people" | "flow" | "plan";

const PAGE_SIZE = 100;
const LANE_COLOURS = ["#b47d08", "#147c88", "#7150a6", "#3b7a57", "#aa4f3b"];
const todayStart = () => format(startOfDay(new Date()), "yyyy-MM-dd");
const todayEnd = () => format(endOfDay(new Date()), "yyyy-MM-dd");
const thisWeekStart = () =>
  format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
const thisWeekEnd = () =>
  format(
    addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 4),
    "yyyy-MM-dd"
  );
const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 31, fontSize: "0.875rem" }),
  menu: (base: any) => ({ ...base, fontSize: "0.875rem" }),
};

const rangeParams = (dateFrom: string, dateTo: string) => {
  const params = new URLSearchParams();
  if (dateFrom) params.set("dateFrom", new Date(dateFrom).toISOString());
  if (dateTo) {
    const end = new Date(dateTo);
    end.setHours(23, 59, 59, 999);
    params.set("dateTo", end.toISOString());
  }
  return params;
};

const formatMinutes = (minutes: number) =>
  minutes < 60
    ? `${minutes}m`
    : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
const metricName = (value: string) =>
  WORK_PIPELINE_STAGE_LABELS[
    value as keyof typeof WORK_PIPELINE_STAGE_LABELS
  ] ??
  value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const outputFor = (row: IWorkPipelineProductivityRow) =>
  row.completed + row.hunts + row.entityEdits;
const MetricHint: React.FC<{ label: string; explanation: string }> = ({
  label,
  explanation,
}) => (
  <span title={explanation} className="text-decoration-underline">
    {label} <HelpCircle size={12} className="text-muted" />
  </span>
);

export const WorkPipelineProductivity: React.FC = () => {
  const [searchParams] = useSearchParams();
  const openToday = searchParams.get("today") === "true";
  const { data: users } = useGet<IUser[]>("qa-lists/users/all", true);
  const [tab, setTab] = useState<ProductivityTab>("overview");
  const [actorDraft, setActorDraft] = useState("");
  const [fromDraft, setFromDraft] = useState(
    openToday ? todayStart() : thisWeekStart()
  );
  const [toDraft, setToDraft] = useState(
    openToday ? todayEnd() : thisWeekEnd()
  );
  const [actor, setActor] = useState("");
  const [dateFrom, setDateFrom] = useState(fromDraft);
  const [dateTo, setDateTo] = useState(toDraft);
  const [selectedActor, setSelectedActor] = useState<string | null>(null);
  const [testerSearch, setTesterSearch] = useState("");

  const prodUrl = (() => {
    const params = rangeParams(dateFrom, dateTo);
    if (actor) params.set("actor", actor);
    return `work-pipeline/productivity?${params.toString()}`;
  })();
  const activityUrl = (() => {
    const params = rangeParams(dateFrom, dateTo);
    params.set("page", "1");
    params.set("limit", String(PAGE_SIZE));
    if (actor) params.set("actor", actor);
    return `work-pipeline/activity?${params.toString()}`;
  })();
  const {
    data: rows,
    loading,
    refetch,
  } = useGet<IWorkPipelineProductivityRow[]>(prodUrl, true);
  const { data: activity, refetch: refetchActivity } =
    useGet<IActivityResponse>(activityUrl, true);

  const apply = () => {
    setActor(actorDraft);
    setDateFrom(fromDraft);
    setDateTo(toDraft);
    setSelectedActor(null);
  };
  const setPreset = (days: number) => {
    const from = format(
      days === 0 ? new Date() : subDays(new Date(), days - 1),
      "yyyy-MM-dd"
    );
    const to = todayEnd();
    setFromDraft(from);
    setToDraft(to);
    setDateFrom(from);
    setDateTo(to);
    setSelectedActor(null);
  };
  const setRange = (from: Date, to: Date) => {
    const fromValue = format(from, "yyyy-MM-dd");
    const toValue = format(to, "yyyy-MM-dd");
    setFromDraft(fromValue);
    setToDraft(toValue);
    setDateFrom(fromValue);
    setDateTo(toValue);
    setSelectedActor(null);
  };
  const setWeekPreset = (previousWeek: boolean) => {
    const monday = startOfWeek(
      previousWeek ? subWeeks(new Date(), 1) : new Date(),
      { weekStartsOn: 1 }
    );
    setRange(monday, addDays(monday, 4));
  };

  const userOptions = (users ?? []).map((user) => ({
    value: user._id.toString(),
    label: user.username,
  }));
  const people = new Map<string, IPersonSummary>();
  for (const row of rows ?? []) {
    const existing = people.get(row.actor);
    if (existing) {
      existing.completed += row.completed;
      existing.hunts += row.hunts;
      existing.edits += row.entityEdits;
      existing.activeMinutes += row.activeMinutes;
      existing.sessions += row.sessions;
      existing.domainsTouched += row.domainsTouched;
      existing.output += outputFor(row);
      existing.rows.push(row);
      for (const [lane, count] of Object.entries(row.buckets)) {
        existing.buckets[lane] = (existing.buckets[lane] ?? 0) + count;
      }
      for (const [lane, minutes] of Object.entries(row.bucketMinutes)) {
        existing.bucketMinutes[lane] =
          (existing.bucketMinutes[lane] ?? 0) + minutes;
      }
    } else {
      people.set(row.actor, {
        actor: row.actor,
        name:
          users?.find((user) => user._id === row.actor)?.username ??
          row.actorName,
        completed: row.completed,
        hunts: row.hunts,
        edits: row.entityEdits,
        activeMinutes: row.activeMinutes,
        sessions: row.sessions,
        domainsTouched: row.domainsTouched,
        output: outputFor(row),
        outputPerHour: 0,
        buckets: { ...row.buckets },
        bucketMinutes: { ...row.bucketMinutes },
        rows: [row],
      });
    }
  }
  const personRows = Array.from(people.values())
    .map((person) => ({
      ...person,
      outputPerHour: person.activeMinutes
        ? Number((person.output / (person.activeMinutes / 60)).toFixed(2))
        : 0,
    }))
    .sort(
      (first, second) =>
        second.output - first.output || first.name.localeCompare(second.name)
    );
  const visiblePeople = personRows.filter((person) =>
    person.name.toLowerCase().includes(testerSearch.toLowerCase())
  );
  const totalOutput = personRows.reduce(
    (sum, person) => sum + person.output,
    0
  );
  const totalMinutes = personRows.reduce(
    (sum, person) => sum + person.activeMinutes,
    0
  );
  const benchmark = totalMinutes
    ? Number((totalOutput / (totalMinutes / 60)).toFixed(2))
    : 0;
  const activePeople = personRows.filter(
    (person) => person.activeMinutes > 0
  ).length;
  const lanes = new Map<
    string,
    { lane: string; output: number; minutes: number }
  >();
  for (const row of rows ?? []) {
    for (const [lane, count] of Object.entries(row.buckets)) {
      const value = lanes.get(lane) ?? { lane, output: 0, minutes: 0 };
      value.output += count;
      value.minutes += row.bucketMinutes[lane] ?? 0;
      lanes.set(lane, value);
    }
  }
  const laneRows = Array.from(lanes.values()).sort(
    (first, second) => second.output - first.output
  );
  const selectedPerson =
    personRows.find((person) => person.actor === selectedActor) ?? null;
  const alerts = personRows.filter(
    (person) =>
      person.activeMinutes >= 60 &&
      benchmark > 0 &&
      person.outputPerHour < benchmark * 0.65
  );
  const trend = [...(rows ?? [])].reduce<Record<string, number>>(
    (days, row) => {
      days[row.day] = (days[row.day] ?? 0) + outputFor(row);
      return days;
    },
    {}
  );
  const trendRows = Object.entries(trend)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([day, output]) => ({
      day: format(new Date(`${day}T12:00:00`), "EEE"),
      output,
    }));
  const laneTotal = laneRows.reduce((sum, lane) => sum + lane.output, 0);

  const renderPeople = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">
            Team roster{" "}
            <Badge bg="light" text="dark">
              {personRows.length} testers
            </Badge>
          </h5>
          <small className="text-muted">
            Select a tester to inspect their daily output and recorded activity.
          </small>
        </div>
        <Form.Control
          size="sm"
          placeholder="Search tester..."
          value={testerSearch}
          onChange={(event) => setTesterSearch(event.target.value)}
          style={{ width: 180 }}
        />
      </div>
      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Tester</th>
                <th>Tracked output / claimed hour</th>
                <th>
                  <MetricHint
                    label="Claimed work time"
                    explanation="The total time from claiming a domain until it is closed or released. Separate claims can overlap, so this is a workflow duration rather than active working time."
                  />
                </th>
                <th>
                  <MetricHint
                    label="Tracked output"
                    explanation="Completed work, code hunts, and entity edits recorded in the selected range."
                  />
                </th>
                <th>Lane mix</th>
                <th aria-label="View detail" />
              </tr>
            </thead>
            <tbody>
              {visiblePeople.map((person) => {
                const eventCount = Object.values(person.buckets).reduce(
                  (sum, count) => sum + count,
                  0
                );
                return (
                  <tr
                    key={person.actor}
                    role="button"
                    onClick={() => setSelectedActor(person.actor)}
                  >
                    <td className="fw-semibold">
                      {person.name}
                      <small className="text-muted d-block">
                        <MetricHint
                          label={`${person.domainsTouched} domains touched`}
                          explanation="Distinct domains with at least one recorded pipeline event in the selected range. This is not the number of claims."
                        />
                      </small>
                    </td>
                    <td>
                      <span className="fw-semibold">
                        {person.outputPerHour}/hr
                      </span>
                      <small className="text-muted d-block">
                        tracked events per claimed hour
                      </small>
                    </td>
                    <td>
                      {formatMinutes(person.activeMinutes)}
                      <small className="text-muted d-block">
                        <MetricHint
                          label={`${person.sessions} recorded sessions`}
                          explanation="Each recorded session is a claim-to-close, release, or code-hunt interval. Intervals can overlap when more than one domain is claimed."
                        />
                      </small>
                    </td>
                    <td>
                      {person.output}
                      <small className="text-muted d-block">
                        {person.completed} completed, {person.hunts} hunts,{" "}
                        {person.edits} edits
                      </small>
                    </td>
                    <td style={{ minWidth: 240 }}>
                      <div
                        className="d-flex overflow-hidden"
                        style={{ height: 8, borderRadius: 4 }}
                      >
                        {Object.entries(person.buckets).map(
                          ([lane, value], index) => (
                            <span
                              key={lane}
                              title={`${metricName(lane)}: ${value} events`}
                              style={{
                                width: `${eventCount ? Math.max(5, (value / eventCount) * 100) : 0}%`,
                                background:
                                  LANE_COLOURS[index % LANE_COLOURS.length],
                              }}
                            />
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Offcanvas
        show={!!selectedPerson}
        onHide={() => setSelectedActor(null)}
        placement="end"
        style={{ width: "min(680px, 100vw)" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{selectedPerson?.name}'s activity</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedPerson && (
            <>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <Card>
                    <Card.Body className="py-2">
                      <small className="text-muted d-block">
                        Tracked output
                      </small>
                      <strong>{selectedPerson.output}</strong>
                      <small className="text-muted d-block">
                        {selectedPerson.completed} completed,{" "}
                        {selectedPerson.hunts} hunts, {selectedPerson.edits}{" "}
                        edits
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6}>
                  <Card>
                    <Card.Body className="py-2">
                      <small className="text-muted d-block">
                        Claimed work time
                      </small>
                      <strong>
                        {formatMinutes(selectedPerson.activeMinutes)}
                      </strong>
                      <small className="text-muted d-block">
                        {selectedPerson.sessions} recorded sessions
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <h6>Lane activity</h6>
              {Object.entries(selectedPerson.bucketMinutes).map(
                ([lane, minutes]) => (
                  <div
                    key={lane}
                    className="d-flex justify-content-between border-bottom py-2"
                  >
                    <span>{metricName(lane)}</span>
                    <small className="text-muted">
                      {selectedPerson.buckets[lane] ?? 0} events,{" "}
                      {formatMinutes(minutes)}
                    </small>
                  </div>
                )
              )}
              <h6 className="mt-4">Daily breakdown</h6>
              <Table responsive size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Output</th>
                    <th>Claimed time</th>
                    <th>Completed</th>
                    <th>Hunts</th>
                    <th>Edits</th>
                    <th>Longest</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPerson.rows.map((row) => (
                    <tr key={row.day}>
                      <td>{row.day}</td>
                      <td>{outputFor(row)}</td>
                      <td>{formatMinutes(row.activeMinutes)}</td>
                      <td>{row.completed}</td>
                      <td>{row.hunts}</td>
                      <td>{row.entityEdits}</td>
                      <td>{formatMinutes(row.longestSessionMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/work-pipeline"
            className="btn btn-sm btn-outline-secondary"
          >
            <ArrowLeft size={14} /> Board
          </Link>
          <div>
            <h4 className="mb-0">Productivity Centre</h4>
            <small className="text-muted">
              Work completed, per person and per task. Claimed-work timestamps
              show where work stays open.
            </small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Link
            to="/work-pipeline/audit"
            className="btn btn-sm btn-outline-secondary"
          >
            Audit Log
          </Link>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              refetch();
              refetchActivity();
            }}
          >
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
        </div>
      </div>
      <Nav
        variant="underline"
        className="mb-3 border-bottom"
        activeKey={tab}
        onSelect={(key) => setTab(key as ProductivityTab)}
      >
        <Nav.Item>
          <Nav.Link eventKey="overview">Overview</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="people">People</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="flow">Pipeline Flow</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="plan">Improvement Plan</Nav.Link>
        </Nav.Item>
      </Nav>
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small mb-1">Tester</Form.Label>
              <Select
                options={userOptions}
                value={
                  actorDraft
                    ? (userOptions.find(
                        (option) => option.value === actorDraft
                      ) ?? null)
                    : null
                }
                onChange={(option) => setActorDraft(option?.value ?? "")}
                isClearable
                placeholder="Everyone..."
                styles={selectStyles}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">From</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={fromDraft}
                onChange={(event) => setFromDraft(event.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">To</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={toDraft}
                onChange={(event) => setToDraft(event.target.value)}
              />
            </Col>
            <Col md="auto">
              <Button size="sm" variant="primary" onClick={apply}>
                <Search size={14} className="me-1" /> Apply
              </Button>
            </Col>
            <Col md="auto">
              <Dropdown as={ButtonGroup} align="end">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setWeekPreset(false)}
                >
                  This week
                </Button>
                <Dropdown.Toggle
                  split
                  size="sm"
                  variant="outline-secondary"
                  aria-label="Choose productivity date range"
                />
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setPreset(0)}>
                    Today
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() =>
                      setRange(subDays(new Date(), 1), subDays(new Date(), 1))
                    }
                  >
                    Yesterday
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setWeekPreset(false)}>
                    This week (Monday-Friday)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setWeekPreset(true)}>
                    Last week (Monday-Friday)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => setPreset(7)}>
                    Last 7 days
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setPreset(30)}>
                    Last 30 days
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" size="sm" /> Loading productivity...
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <>
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <small className="text-muted d-block">Team members</small>
                      <strong className="fs-4">{activePeople}</strong>
                      <small className="text-muted d-block">
                        with recorded work
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <small className="text-muted d-block">
                        Team throughput
                      </small>
                      <strong className="fs-4">{totalOutput}</strong>
                      <small className="text-muted d-block">
                        output this range
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <small className="text-muted d-block">
                        Average output / hour
                      </small>
                      <strong className="fs-4">{benchmark}</strong>
                      <small className="text-muted d-block">
                        per claimed work hour
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <small className="text-muted d-block">Open alerts</small>
                      <strong
                        className={
                          alerts.length
                            ? "fs-4 text-warning-emphasis"
                            : "fs-4 text-success"
                        }
                      >
                        {alerts.length}
                      </strong>
                      <small className="text-muted d-block">
                        needs a manager review
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row className="g-3">
                <Col lg={4}>
                  <Card className="h-100">
                    <Card.Header>Workload distribution</Card.Header>
                    <Card.Body>
                      {laneRows.map((lane, index) => (
                        <div
                          key={lane.lane}
                          className="d-flex align-items-center gap-2 mb-2"
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background:
                                LANE_COLOURS[index % LANE_COLOURS.length],
                            }}
                          />
                          <small className="flex-grow-1">
                            {metricName(lane.lane)}
                          </small>
                          <strong>
                            {laneTotal
                              ? Math.round((lane.output / laneTotal) * 100)
                              : 0}
                            %
                          </strong>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={8}>
                  <Card className="h-100">
                    <Card.Header>Team output, selected range</Card.Header>
                    <Card.Body style={{ height: 210 }}>
                      {trendRows.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={trendRows}>
                            <CartesianGrid
                              vertical={false}
                              strokeDasharray="3 3"
                            />
                            <XAxis dataKey="day" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar
                              dataKey="output"
                              fill="#147c88"
                              radius={[3, 3, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted p-5">
                          No recorded output in this range.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
          {tab === "people" && renderPeople()}
          {tab === "flow" && (
            <>
              <div className="mb-3">
                <h5 className="mb-1">Pipeline flow</h5>
                <small className="text-muted">
                  Events and claimed-work time grouped by the lane recorded at
                  claim.
                </small>
              </div>
              <Card>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Lane</th>
                        <th>Recorded events</th>
                        <th>Share of activity</th>
                        <th>Claimed work time</th>
                        <th>Average time / event</th>
                      </tr>
                    </thead>
                    <tbody>
                      {laneRows.map((lane, index) => (
                        <tr key={lane.lane}>
                          <td>
                            <span
                              className="d-inline-block me-2"
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background:
                                  LANE_COLOURS[index % LANE_COLOURS.length],
                              }}
                            />
                            {metricName(lane.lane)}
                          </td>
                          <td>{lane.output}</td>
                          <td>
                            {laneTotal
                              ? Math.round((lane.output / laneTotal) * 100)
                              : 0}
                            %
                          </td>
                          <td>{formatMinutes(lane.minutes)}</td>
                          <td>
                            {formatMinutes(
                              lane.output
                                ? Math.round(lane.minutes / lane.output)
                                : 0
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
          {tab === "plan" && (
            <>
              <div className="mb-3">
                <h5 className="mb-1">
                  Auto-generated from this range's output
                </h5>
                <small className="text-muted">
                  Signals compare output per claimed-work hour with the selected
                  team's average. They indicate a review point, not a diagnosis.
                </small>
              </div>
              {alerts.length ? (
                <Card border="warning">
                  <Card.Body>
                    <h6 className="text-warning-emphasis">
                      Review {alerts.length} output pattern
                      {alerts.length === 1 ? "" : "s"}
                    </h6>
                    {alerts.map((person) => (
                      <div key={person.actor} className="border-top pt-2 mt-2">
                        <strong>{person.name}</strong>
                        <span className="text-muted">
                          : {person.outputPerHour}/hr, below 65% of the{" "}
                          {benchmark}/hr team benchmark across{" "}
                          {formatMinutes(person.activeMinutes)} of claimed work.
                          Review lane mix, open claims, and recorded blockers
                          before taking action.
                        </span>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              ) : (
                <Card border="success" className="bg-success bg-opacity-10">
                  <Card.Body className="text-success fw-semibold">
                    No one is flagged in this range. Recorded output is within
                    the current review threshold, or there is not enough
                    claimed-work time to compare.
                  </Card.Body>
                </Card>
              )}
              <Card className="mt-3">
                <Card.Header>How the signal works</Card.Header>
                <Card.Body className="small text-muted">
                  A review signal requires at least one hour of claimed work and
                  output per hour below 65% of the selected team's average. Work
                  duration is inferred from claim and closing events; use the
                  activity trail and block reasons for context before drawing
                  conclusions.
                </Card.Body>
              </Card>
            </>
          )}
          {tab === "people" && activity?.results?.length ? (
            <Card className="mt-3">
              <Card.Header className="small text-muted">
                Recent activity trail ({activity.pagination.total} events)
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Who</th>
                      <th>Action</th>
                      <th>Domain</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.results.map((entry, index) => (
                      <tr key={`${entry.at}-${index}`}>
                        <td>{format(new Date(entry.at), "MMM d HH:mm")}</td>
                        <td>
                          {users?.find((user) => user._id === entry.actor)
                            ?.username ?? entry.actorName}
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {metricName(entry.action)}
                          </Badge>
                        </td>
                        <td>{entry.domain ?? "-"}</td>
                        <td className="text-muted">{entry.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
};
